import axios from 'axios'
import { getValidToken, clearToken } from '../utils/token'
import { triggerUpgradeModalFromInterceptor } from '../contexts/UpgradeModalContext'
import type { PlanTier } from './billing'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Global logout handler - can be set by AuthContext
let globalLogoutHandler: (() => void) | null = null

export const setLogoutHandler = (handler: () => void) => {
  globalLogoutHandler = handler
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available (expired tokens are dropped client-side)
    const token = getValidToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Map an endpoint URL back to a human-readable feature name for the
// upgrade prompt. Anything not in the map gets a generic fallback. Kept
// here (and not in plan_guard) so the message stays vendor-neutral on
// the wire — the backend just returns "code: plan_required".
const FEATURE_NAMES: Record<string, string> = {
  '/api/v1/ai/chat': 'AI Assistant',
  '/api/v1/ai/insights': 'AI Insights',
  '/api/v1/ai/predictions': 'AI Predictions',
  '/api/v1/integrations': 'Integrations',
  '/api/v1/dashboard/executive': 'Executive Dashboard',
  '/api/v1/governance/audit-export': 'Audit Export',
  '/api/v1/status-sets': 'Custom Status Sets',
  '/api/v1/portfolio-governance': 'Portfolio Governance',
}

function featureFromUrl(url: string | undefined): string {
  if (!url) return 'This feature'
  // Match the longest prefix so /api/v1/ai/insights wins over /api/v1/ai.
  const sorted = Object.keys(FEATURE_NAMES).sort((a, b) => b.length - a.length)
  for (const prefix of sorted) {
    if (url.startsWith(prefix)) return FEATURE_NAMES[prefix]
  }
  return 'This feature'
}

/** Routes a logged-out visitor may use; never redirected to /login. */
const PUBLIC_PATH_PREFIXES = [
  '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email',
  '/accept-invitation', '/share/', '/guest/', '/sso/', '/approve',
]

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`))
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    if (status === 401) {
      // Handle unauthorized access - session expired
      clearToken()

      // Call global logout handler if available (clears user state)
      if (globalLogoutHandler) {
        globalLogoutHandler()
      }

      // Send the user to /login only when a signed-in session actually
      // expired. A logged-out visitor on a public page (signup, password
      // reset, email verification, invitations, share and guest links)
      // must stay where they are — a stray 401 there used to bounce them
      // to /login, which made self-serve signup unreachable.
      const hadSession = Boolean(error.config?.headers?.Authorization)
      if (hadSession && !isPublicPath(window.location.pathname)) {
        window.location.href = '/login'
      }
    } else if (status === 402) {
      // Plan-gated feature — surface the global upgrade modal instead
      // of bubbling a raw HTTP error to every caller.
      // Backend shape (from plan_guard.py):
      //   { code, required_tier, current_tier, message, upgrade_plan, upgrade_url }
      const detail = error.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.code) {
        const requiredTier = (detail.required_tier || 'growth') as PlanTier
        const url = error.config?.url
        triggerUpgradeModalFromInterceptor({
          requiredTier,
          feature: featureFromUrl(url),
          description: detail.message,
        })
      }
    }
    return Promise.reject(error)
  }
)
